import {CommonModule} from '@angular/common';
import {Component, DestroyRef, OnInit, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {
  ConfigService,
  FetchAndStoreRecordLocatorResultsInVaultProfile,
} from '@shared-library';
import {
  FdkIllustrationComponent,
  FdkLoadingDotsComponent,
  FdkIllustrationName,
} from 'fasten-design-kit';
import {NGXLogger} from 'ngx-logger';
import {concat, Subject, take, takeUntil, timer} from 'rxjs';

import {FastenService} from '../../services/fasten.service';

@Component({
  selector: 'app-record-locator-loading',
  standalone: true,
  imports: [
    CommonModule,
    FdkIllustrationComponent,
    FdkLoadingDotsComponent,
  ],
  templateUrl: './record-locator-loading.component.html',
  styleUrls: ['./record-locator-loading.component.css'],
})
export class RecordLocatorLoadingComponent implements OnInit {
  loadingTefcaRLS = true;
  readonly loadingMessages: ReadonlyArray<{
    illustration: FdkIllustrationName;
    title: string;
    description: string;
  }> = [
    {
      illustration: 'connecting',
      title: 'Finding your health systems',
      description: "We're looking for the providers you've visited. This can take up to two minutes.",
    },
    {
      illustration: 'location',
      title: 'Looking across health systems',
      description: "We're checking for providers linked to your details. You don't need to do anything yet.",
    },
    {
      illustration: 'identifying',
      title: 'Looking for the right matches',
      description: "We use your verified details to look for providers you've visited.",
    },
    {
      illustration: 'empty',
      title: 'Giving health systems time to respond',
      description: "Some health systems take a little longer to respond. We're still looking.",
    },
    {
      illustration: 'connecting',
      title: 'Keeping the search going',
      description: 'You can stay on this page while we look for your providers.',
    },
    {
      illustration: 'sign-in',
      title: 'Your next step is choosing providers',
      description: 'When the search finishes, you can choose which providers to connect.',
    },
  ];
  currentMessageIndex = 0;
  previousMessageIndex: number | null = null;

  get visibleMessageIndices(): number[] {
    return this.previousMessageIndex === null
      ? [this.currentMessageIndex]
      : [this.previousMessageIndex, this.currentMessageIndex];
  }

  private readonly destroyRef = inject(DestroyRef);
  private readonly loadingFinished = new Subject<void>();

  constructor(
    private readonly configService: ConfigService,
    private readonly fastenService: FastenService,
    private readonly router: Router,
    private readonly logger: NGXLogger,
  ) {}

  ngOnInit(): void {
    if (this.configService.vaultProfileConfig$.rlsQueryComplete) {
      this.finishLoading();
      return;
    }

    // Rotate at 4, 8, ... 28 seconds, then at 38, 46, ... seconds:
    // the slower cadence starts eight seconds after the 30-second boundary.
    concat(
      timer(4_000, 4_000).pipe(take(7)),
      timer(10_000, 8_000),
    ).pipe(
      takeUntil(this.loadingFinished),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.previousMessageIndex = this.currentMessageIndex;
      this.currentMessageIndex = (this.currentMessageIndex + 1) % this.loadingMessages.length;
      timer(250).pipe(
        takeUntil(this.loadingFinished),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(() => this.previousMessageIndex = null);
    });

    FetchAndStoreRecordLocatorResultsInVaultProfile(
      this.configService,
      () => this.fastenService.recordLocatorRegisterAndPollForStatus(),
      (taskId) => this.fastenService.recordLocatorResults(taskId),
    ).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: () => this.finishLoading(),
      error: (error) => {
        this.logger.error('Error fetching record locator results', error);
        this.configService.vaultProfileConfig = {
          rlsQueryComplete: true,
        };
        this.finishLoading();
      },
    });
  }

  private finishLoading(): void {
    this.loadingFinished.next();
    this.loadingFinished.complete();
    this.previousMessageIndex = null;
    this.loadingTefcaRLS = false;
    void this.router.navigateByUrl('/dashboard', {
      replaceUrl: true,
      state: {skipIdentityVerification: true},
    });
  }
}
